import { useState, useEffect } from "react";
import { useSetAtom, useAtomValue } from "jotai";
import { Navigate, useLocation } from "react-router-dom";
import { authService, logSessionExpired } from "../services/auth";
import {
  sessionExpiredAtom,
  isAdminAtom,
  userTypeAtom,
  agreementAcceptedAtom,
} from "../atoms/authAtoms";
import { loadCognitoConfig, isLocalDevMode } from "../config/cognito";
import { isInternalRole } from "../services/roles";
import { decodeToken } from "../utils/tokenUtils";
import {
  reviewerAllowedCohorts,
  reviewerCanManageLabeling,
  reviewerHasProjectAccess,
  reviewerHomePath,
} from "../utils/reviewerNav";
import { FullScreenLoading } from "./common/LoadingStates";
import { useDelayedFlag } from "../hooks/useDelayedFlag";
import { isNotifyPath } from "../notify/paths";

/** Reviewers are pilot-bound to their cohort. They can only see /review/* and
 * the case-viewer routes (which the backend's allowed_cohorts filter also
 * gates server-side), except for /upload when their JWT carries the explicit
 * upload capability. Everything else routes them back to their cohort.
 *
 * Shared post-auth utility routes (`/agreement`, `/change-password`) are
 * wrapped by PrivateRoute too, so they must be allowed explicitly — otherwise
 * a new reviewer loops between `/agreement` (forced by the agreement gate)
 * and `/review/cohort/<id>` (forced by this gate).
 *
 * `/notify` and `/notify/*` are the a2z Notify app (simulation alerts): pilot
 * radiologists may be reviewers here, and the Notify API checks its own
 * directory of people. `isNotifyPath` is the rule shared with App.tsx and
 * public/notify/sw.js.
 *
 * Hybrid reviewer/annotator: when the JWT also carries allowed_projects,
 * admin has granted additive annotator access. The caller skips this gate
 * entirely (see `reviewerHasProjectAccess`), since backend filters still
 * enforce project-level access. */
const REVIEWER_ALLOWED_PREFIXES = ["/review/", "/case-viewer/"];
const REVIEWER_ALLOWED_EXACT = ["/agreement", "/change-password"];

const reviewerCanVisit = (
  pathname: string,
  canUpload: boolean,
  canManageLabeling: boolean,
): boolean => {
  // A reviewer granted the server-minted `upload` capability may use the
  // self-serve upload flow. Keep this exception exact: no other reviewer
  // route is widened, and UploadRoute still enforces the same capability.
  if (pathname === "/upload" && canUpload) return true;
  if (canManageLabeling && (pathname === "/labeling" || pathname.startsWith("/labeling/"))) {
    return true;
  }
  if (
    canManageLabeling &&
    (pathname === "/labeling-overview" || pathname.startsWith("/labeling-overview/"))
  ) {
    return true;
  }
  if (REVIEWER_ALLOWED_EXACT.includes(pathname) || isNotifyPath(pathname)) return true;
  if (
    !REVIEWER_ALLOWED_PREFIXES.some(
      (p) => pathname === p.replace(/\/$/, "") || pathname.startsWith(p),
    )
  ) {
    return false;
  }
  // Defense in depth on /review/cohort/<id>: only allow paths whose cohort
  // segment is in the reviewer's allowed list. Backend cohort filter is the
  // real gate; this just avoids a half-loaded page before the 403 lands.
  // /case-viewer/<studyUid> has no cohort segment so we defer to the BE.
  const reviewMatch = pathname.match(/^\/review\/cohort\/([^/]+)/);
  if (reviewMatch) {
    const cohort = decodeURIComponent(reviewMatch[1] || "");
    return reviewerAllowedCohorts().includes(cohort);
  }
  return true;
};

export const PrivateRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const setSessionExpired = useSetAtom(sessionExpiredAtom);
  const location = useLocation();
  const userType = useAtomValue(userTypeAtom);
  const agreementAccepted = useAtomValue(agreementAcceptedAtom);
  const [authStatus, setAuthStatus] = useState<"checking" | "authenticated" | "unauthenticated">(
    "checking",
  );
  const showVerifying = useDelayedFlag(authStatus === "checking", 300);

  useEffect(() => {
    const checkAuth = async () => {
      // A live JWT needs no OAuth configuration. Keep the local-dev sentinel
      // on the config path so its userId requirement and dev mode still load.
      if (
        authService.getStoredAuth().token !== "local-dev" &&
        authService.evaluateSession() === "active"
      ) {
        setAuthStatus("authenticated");
        return;
      }

      // Load Cognito config (needed for isLocalDevMode check).
      // If it fails, proceed with production auth flow — don't block.
      try {
        await loadCognitoConfig();
      } catch {
        /* proceed as production */
      }

      // Local dev: just need userId
      if (isLocalDevMode()) {
        const { userId } = authService.getStoredAuth();
        setAuthStatus(userId ? "authenticated" : "unauthenticated");
        return;
      }

      const state = authService.evaluateSession();

      // Live token (covers a fresh impersonation token, which carries no
      // refresh by design) — authenticated.
      if (state === "active") {
        setAuthStatus("authenticated");
        return;
      }

      // Token expired/absent but a refresh token survives — try silent refresh.
      if (state === "refreshable") {
        try {
          const result = await authService.refreshToken();
          if (result) {
            setAuthStatus("authenticated");
            return;
          }
        } catch {
          // Refresh failed - fall through to unauthenticated
        }
      }

      // A session existed but can't continue → surface the modal. "absent"
      // (never logged in here) redirects to /login silently, no modal.
      if (state !== "absent") {
        logSessionExpired("private-route", state);
        setSessionExpired(true);
      }
      setAuthStatus("unauthenticated");
    };

    checkAuth();
  }, [setSessionExpired]);

  // Show loading while checking/refreshing — only after 300ms to avoid
  // flashing on fast (valid-token) paths.
  if (authStatus === "checking") {
    return showVerifying ? <FullScreenLoading message="Verifying session" /> : null;
  }

  // Not authenticated - redirect to login, preserving intended destination
  if (authStatus === "unauthenticated") {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  // Agreement not yet signed for this user_type — bounce to /agreement.
  // Strict `=== false` check: admin/client return null and shouldn't be gated.
  // Skip when already on /agreement to avoid a render loop.
  if (agreementAccepted === false && location.pathname !== "/agreement") {
    return <Navigate to="/agreement" replace state={{ from: location }} />;
  }

  // Reviewers are pilot-bound — bounce them back to their cohort if they
  // wander onto /worklist, /labeling, /reporter, /admin (AdminRoute blocks
  // admin separately), etc. Backend cohort filter also enforces this.
  // Exception: a reviewer with allowed_projects has been granted additive
  // annotator access — let them roam; BE still filters per project.
  const { token } = authService.getStoredAuth();
  const reviewerCanUpload = Boolean(token && decodeToken(token)?.capabilities?.includes("upload"));
  const reviewerCanManage = reviewerCanManageLabeling();

  if (
    userType === "reviewer" &&
    !reviewerHasProjectAccess() &&
    !reviewerCanVisit(location.pathname, reviewerCanUpload, reviewerCanManage)
  ) {
    const home = reviewerHomePath();
    if (home) {
      if (location.pathname !== home) return <Navigate to={home} replace />;
    } else {
      // Defense in depth: reviewer JWT carries no allowed_cohorts (backend
      // should never issue this; if it does, don't fall through to the
      // requested route — bounce to /login so the session resets).
      return <Navigate to="/login" replace />;
    }
  }

  // Authenticated - render children
  return <>{children}</>;
};

/** Route guard for admin-only pages. Redirects non-admins to the worklist. */
export const AdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const isAdmin = useAtomValue(isAdminAtom);
  return <PrivateRoute>{isAdmin ? children : <Navigate to="/worklist" replace />}</PrivateRoute>;
};

/** /upload is internal-or-granted: internal staff always, external users only
 * with the `upload` capability (JWT claim). Everyone else is redirected —
 * gate parity with the Navbar link and the backend `verify_can_upload`. */
export const UploadRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const userType = useAtomValue(userTypeAtom);
  const { token } = authService.getStoredAuth();
  const canUpload =
    isInternalRole(userType) ||
    Boolean(token && decodeToken(token)?.capabilities?.includes("upload"));
  return <PrivateRoute>{canUpload ? children : <Navigate to="/worklist" replace />}</PrivateRoute>;
};
